import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  BarList, Button, Card, Tab, TabGroup, TabList,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow,
  Text, Title,
} from '@tremor/react';
import { fetchCashReport } from '../api/cashReport';
import { getCashboxStatus } from '../api/cashbox';
import { currentUser } from '../api/auth';
import AccessDenied from '../components/AccessDenied';
import CajaSessions from '../components/CajaSessions';
import CashTicketsModal from '../components/CashTicketsModal';
import DateRangePresets from '../components/DateRangePresets';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import KpiTile from '../components/KpiTile';
import PrintHeader from '../components/PrintHeader';
import TableSkeleton from '../components/TableSkeleton';
import { useStoreFilter } from '../lib/storeFilter';
import { localYMD } from '../lib/format';
import { exportReportPdf } from '../lib/pdf';
import { t } from '../lib/i18n';
import type { CashReport, User, CashboxStatus } from '../api/types';

// MXN formatter — 0 decimal places for KPI display.
const fmtMxn = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(n);

// 2 decimal places for % column.
const fmtPct = (n: number) => `${(n * 100).toFixed(1)}%`;

/** First day of the current month as YYYY-MM-DD. */
function firstOfMonth(): string {
  const d = new Date();
  return localYMD(new Date(d.getFullYear(), d.getMonth(), 1));
}

/** CSV field escaper — quotes fields containing commas, double-quotes, or newlines. */
function csvEsc(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

type ViewMode = 'method' | 'day' | 'cashier';
const VIEWS: ViewMode[] = ['method', 'day', 'cashier'];

// Drill-down modal state.
interface DrillState {
  open: boolean;
  title: string;
  date?: string;
  tender?: string;
  cashier?: string;
}

// --------------------------------------------------------------------------

export default function Caja() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <CajaInner user={user} /> : <Navigate to="/login" replace />;
}

// --------------------------------------------------------------------------

function CajaInner({ user }: { user: User }) {
  const isAuthorized =
    user.role === 'owner' || user.role === 'manager' || user.role === 'accounting';

  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(() => localYMD());
  // Shared header store (lib/storeFilter) — persists across pages.
  const [sid] = useStoreFilter(user);
  const [view, setView] = useState<ViewMode>('method');
  const [data, setData] = useState<CashReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [drill, setDrill] = useState<DrillState>({ open: false, title: '' });

  // Cash-box session module: probe capability; when enabled, a toggle switches
  // between the read-only corte and the "Sesiones de caja" write workspace.
  const [cbStatus, setCbStatus] = useState<CashboxStatus | null>(null);
  const [mode, setMode] = useState<'corte' | 'sesiones'>('corte');
  useEffect(() => {
    let alive = true;
    getCashboxStatus().then((s) => { if (alive) setCbStatus(s); }).catch(() => { /* module stays hidden */ });
    return () => { alive = false; };
  }, []);

  const load = () => {
    if (!isAuthorized) return;
    let alive = true;
    setErr(null);
    setLoading(true);
    fetchCashReport({ from, to, sid: sid === null ? undefined : sid })
      .then((r) => { if (alive) setData(r); })
      .catch((e: Error) => { if (alive) setErr(e.message ?? String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(() => { load(); }, [isAuthorized, from, to, sid]);

  // ---------- derived pivot data ----------

  // tender code → name map + ordered list of codes (by amount desc).
  const tenders = useMemo(() => {
    if (!data) return [];
    return [...data.by_tender].sort((a, b) => b.amount - a.amount);
  }, [data]);

  const tenderNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of tenders) m.set(r.code, r.name);
    return m;
  }, [tenders]);

  const tenderCodes = useMemo(() => tenders.map((r) => r.code), [tenders]);

  // Efectivo KPI: row whose code==='EF' OR name starts with 'efectivo' (case-insensitive).
  const efectivoAmount = useMemo(() => {
    if (!data) return 0;
    const row = data.by_tender.find(
      (r) => r.code === 'EF' || r.name.toLowerCase().startsWith('efectivo'),
    );
    return row?.amount ?? 0;
  }, [data]);

  // by_day pivot: Map<date, Map<code, amount>>
  const dayPivot = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    if (!data) return m;
    for (const r of data.by_day) {
      if (!m.has(r.date)) m.set(r.date, new Map());
      m.get(r.date)!.set(r.code, r.amount);
    }
    return m;
  }, [data]);

  // Distinct dates descending.
  const sortedDates = useMemo(
    () => [...dayPivot.keys()].sort((a, b) => (a < b ? 1 : -1)),
    [dayPivot],
  );

  // by_cashier pivot: Map<id, {name, totals: Map<code, amount>}>
  const cashierPivot = useMemo(() => {
    const m = new Map<number, { name: string; totals: Map<string, number> }>();
    if (!data) return m;
    for (const r of data.by_cashier) {
      if (!m.has(r.cashier_id)) {
        m.set(r.cashier_id, { name: r.cashier_name, totals: new Map() });
      }
      const c = m.get(r.cashier_id)!;
      c.totals.set(r.code, (c.totals.get(r.code) ?? 0) + r.amount);
    }
    return m;
  }, [data]);

  // Cashiers sorted by total desc.
  const sortedCashiers = useMemo(() => {
    const out: { id: number; name: string; totals: Map<string, number>; grand: number }[] = [];
    for (const [id, { name, totals }] of cashierPivot.entries()) {
      const grand = [...totals.values()].reduce((s, v) => s + v, 0);
      out.push({ id, name, totals, grand });
    }
    return out.sort((a, b) => b.grand - a.grand);
  }, [cashierPivot]);

  // BarList data for DonutChart on Por-método tab.
  const tenderBars = useMemo(
    () => tenders.map((r) => ({ name: r.name, value: r.amount })),
    [tenders],
  );

  // BarList data for Por-cajero tab.
  const cashierBars = useMemo(
    () => sortedCashiers.slice(0, 12).map((c) => ({ name: c.name, value: c.grand })),
    [sortedCashiers],
  );

  // ---------- CSV export ----------

  function downloadCsv() {
    if (!data) return;
    const rows: string[] = [];
    const total = data.totals.amount;

    if (view === 'method') {
      rows.push([t.caja.cols.method, t.caja.cols.tickets, t.caja.cols.amount, t.caja.cols.pct].map(csvEsc).join(','));
      for (const r of tenders) {
        const pct = total > 0 ? fmtPct(r.amount / total) : '0.0%';
        rows.push([r.name, r.count, r.amount, pct].map(csvEsc).join(','));
      }
    } else if (view === 'day') {
      const header = [t.caja.cols.date, ...tenderCodes.map((c) => tenderNames.get(c) ?? c), t.caja.cols.total];
      rows.push(header.map(csvEsc).join(','));
      for (const date of sortedDates) {
        const byCode = dayPivot.get(date) ?? new Map();
        const rowTotal = tenderCodes.reduce((s, c) => s + (byCode.get(c) ?? 0), 0);
        rows.push([date, ...tenderCodes.map((c) => byCode.get(c) ?? 0), rowTotal].map(csvEsc).join(','));
      }
    } else {
      // cashier
      const header = [t.caja.cols.cashier, ...tenderCodes.map((c) => tenderNames.get(c) ?? c), t.caja.cols.total];
      rows.push(header.map(csvEsc).join(','));
      for (const c of sortedCashiers) {
        rows.push([c.name, ...tenderCodes.map((code) => c.totals.get(code) ?? 0), c.grand].map(csvEsc).join(','));
      }
    }

    const blob = new Blob(['﻿' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `corte_caja_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---------- PDF export ----------
  // Branded PDF of the active view's table (mirrors downloadCsv's shaping;
  // money cells are MXN-formatted). Day/cashier pivots can be wide → landscape.
  function downloadPdf() {
    if (!data) return;
    const total = data.totals.amount;
    const meta = [`${t.caja.views[view]} · ${from} – ${to}${sid !== null ? ` · Tienda ${sid}` : ''}`];
    const rows: Array<Array<string | number>> = [];
    const emphasizeRows: number[] = [];
    let columns: string[];
    let numericColumns: number[];
    let orientation: 'portrait' | 'landscape' = 'portrait';

    if (view === 'method') {
      columns = [t.caja.cols.method, t.caja.cols.tickets, t.caja.cols.amount, t.caja.cols.pct];
      for (const r of tenders) {
        const pct = total > 0 ? fmtPct(r.amount / total) : '0.0%';
        rows.push([r.name, r.count, fmtMxn(r.amount), pct]);
      }
      emphasizeRows.push(rows.length);
      rows.push([t.caja.cols.total, data.totals.tickets, fmtMxn(total), total > 0 ? '100.0%' : '0.0%']);
      numericColumns = [1, 2, 3];
    } else if (view === 'day') {
      columns = [t.caja.cols.date, ...tenderCodes.map((c) => tenderNames.get(c) ?? c), t.caja.cols.total];
      for (const date of sortedDates) {
        const byCode = dayPivot.get(date) ?? new Map<string, number>();
        const rowTotal = tenderCodes.reduce((s, c) => s + (byCode.get(c) ?? 0), 0);
        rows.push([date, ...tenderCodes.map((c) => fmtMxn(byCode.get(c) ?? 0)), fmtMxn(rowTotal)]);
      }
      numericColumns = columns.map((_, i) => i).slice(1);
      orientation = 'landscape';
    } else {
      columns = [t.caja.cols.cashier, ...tenderCodes.map((c) => tenderNames.get(c) ?? c), t.caja.cols.total];
      for (const c of sortedCashiers) {
        rows.push([c.name, ...tenderCodes.map((code) => fmtMxn(c.totals.get(code) ?? 0)), fmtMxn(c.grand)]);
      }
      numericColumns = columns.map((_, i) => i).slice(1);
      orientation = 'landscape';
    }

    exportReportPdf({
      title: t.caja.title,
      meta,
      printedBy: user.username,
      columns,
      rows,
      numericColumns,
      emphasizeRows,
      orientation,
      filename: `corte_caja_${view}_${from}_${to}.pdf`,
    });
  }

  // ---------- helpers ----------

  const printSubtitle = `${from} – ${to}${sid !== null ? ` · Tienda ${sid}` : ''}`;
  const viewIdx = VIEWS.indexOf(view);

  const totals = data?.totals ?? { amount: 0, tickets: 0 };
  const avgTicket = totals.tickets > 0 ? totals.amount / totals.tickets : 0;

  const csvDisabled = !data || (
    view === 'method' ? tenders.length === 0 :
    view === 'day' ? sortedDates.length === 0 :
    sortedCashiers.length === 0
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter={isAuthorized} />

      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{t.caja.title}</Title>

        {isAuthorized && cbStatus?.enabled && (
          <div className="flex gap-2 no-print">
            <Button size="xs" variant={mode === 'corte' ? 'primary' : 'secondary'} onClick={() => setMode('corte')}>
              {t.caja.title}
            </Button>
            <Button size="xs" variant={mode === 'sesiones' ? 'primary' : 'secondary'} onClick={() => setMode('sesiones')}>
              {t.caja.sesiones.tab}
            </Button>
          </div>
        )}

        {!isAuthorized ? (
          <AccessDenied user={user} />
        ) : mode === 'sesiones' ? (
          <CajaSessions user={user} />
        ) : (
          <>
            {/* Toolbar (no-print) */}
            <Card className="no-print">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
                <DateRangePresets
                  from={from}
                  to={to}
                  onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }}
                />
                <div className="sm:ml-auto flex items-center gap-2">
                  <Button
                    size="xs"
                    variant="secondary"
                    disabled={csvDisabled}
                    onClick={downloadCsv}
                  >
                    {t.caja.downloadCsv}
                  </Button>
                  <Button
                    size="xs"
                    variant="secondary"
                    disabled={csvDisabled}
                    onClick={downloadPdf}
                  >
                    {t.caja.downloadPdf}
                  </Button>
                </div>
              </div>
            </Card>

            {err ? (
              <ErrorRetry message={err} onRetry={load} />
            ) : loading && !data ? (
              <TableSkeleton rows={5} cols={4} />
            ) : (
              <div className="print-region space-y-4">
                <PrintHeader title={t.caja.title} subtitle={printSubtitle} />

                {/* KPI row — dense 5-up tiles (values fit narrow cards) */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                  <KpiTile dense label={t.caja.kpi.total} value={fmtMxn(totals.amount)} />
                  <KpiTile dense label={t.caja.kpi.cash} value={fmtMxn(efectivoAmount)} />
                  <KpiTile dense label={t.caja.kpi.other} value={fmtMxn(totals.amount - efectivoAmount)} />
                  <KpiTile dense label={t.caja.kpi.tickets} value={String(totals.tickets)} />
                  <KpiTile dense label={t.caja.kpi.avg} value={fmtMxn(avgTicket)} />
                </div>

                {/* View tab bar — content panels are rendered below separately */}
                <TabGroup index={viewIdx} onIndexChange={(i) => setView(VIEWS[i])}>
                  <TabList className="no-print">
                    {VIEWS.map((v) => (
                      <Tab key={v}>{t.caja.views[v]}</Tab>
                    ))}
                  </TabList>
                </TabGroup>

                {/* ── Por método ─────────────────────────────────────────── */}
                {view === 'method' && (
                  <div className="space-y-4">
                    {tenders.length === 0 ? (
                      <Text>{t.caja.empty}</Text>
                    ) : (
                      <>
                        {tenderBars.length > 0 && (
                          <Card>
                            <Title className="text-base">{t.caja.views.method}</Title>
                            <BarList
                              data={tenderBars}
                              valueFormatter={fmtMxn}
                              className="mt-3"
                            />
                          </Card>
                        )}
                        <Card className="p-0 overflow-hidden">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableHeaderCell>{t.caja.cols.method}</TableHeaderCell>
                                <TableHeaderCell className="text-right">{t.caja.cols.tickets}</TableHeaderCell>
                                <TableHeaderCell className="text-right">{t.caja.cols.amount}</TableHeaderCell>
                                <TableHeaderCell className="text-right">{t.caja.cols.pct}</TableHeaderCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tenders.map((r) => (
                                <TableRow
                                  key={r.code}
                                  className="cursor-pointer hover:bg-tremor-background-muted dark:hover:bg-dark-tremor-background-muted"
                                  onClick={() =>
                                    setDrill({
                                      open: true,
                                      title: `${t.caja.drillTitle} — ${r.name}`,
                                      tender: r.code,
                                    })
                                  }
                                >
                                  <TableCell>{r.name}</TableCell>
                                  <TableCell className="text-right">{r.count}</TableCell>
                                  <TableCell className="text-right">{fmtMxn(r.amount)}</TableCell>
                                  <TableCell className="text-right">
                                    {totals.amount > 0 ? fmtPct(r.amount / totals.amount) : '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Card>
                      </>
                    )}
                  </div>
                )}

                {/* ── Por día ────────────────────────────────────────────── */}
                {view === 'day' && (
                  <div className="space-y-4">
                    {sortedDates.length === 0 ? (
                      <Text>{t.caja.empty}</Text>
                    ) : (
                      <>
                        <Card>
                          <Title className="text-base">{t.caja.views.day}</Title>
                          <BarList
                            data={sortedDates.map((date) => {
                              const byCode = dayPivot.get(date) ?? new Map<string, number>();
                              return {
                                name: date,
                                value: tenderCodes.reduce((s, c) => s + (byCode.get(c) ?? 0), 0),
                              };
                            })}
                            valueFormatter={fmtMxn}
                            className="mt-3"
                          />
                        </Card>
                        <Card className="p-0 overflow-hidden">
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableHeaderCell>{t.caja.cols.date}</TableHeaderCell>
                                  {tenderCodes.map((code) => (
                                    <TableHeaderCell key={code} className="text-right">
                                      {tenderNames.get(code) ?? code}
                                    </TableHeaderCell>
                                  ))}
                                  <TableHeaderCell className="text-right">{t.caja.cols.total}</TableHeaderCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedDates.map((date) => {
                                  const byCode = dayPivot.get(date) ?? new Map<string, number>();
                                  const rowTotal = tenderCodes.reduce((s, c) => s + (byCode.get(c) ?? 0), 0);
                                  return (
                                    <TableRow
                                      key={date}
                                      className="cursor-pointer hover:bg-tremor-background-muted dark:hover:bg-dark-tremor-background-muted"
                                      onClick={() =>
                                        setDrill({
                                          open: true,
                                          title: `${t.caja.drillTitle} — ${date}`,
                                          date,
                                        })
                                      }
                                    >
                                      <TableCell>{date}</TableCell>
                                      {tenderCodes.map((code) => (
                                        <TableCell key={code} className="text-right">
                                          {byCode.has(code) ? fmtMxn(byCode.get(code)!) : '—'}
                                        </TableCell>
                                      ))}
                                      <TableCell className="text-right font-medium">{fmtMxn(rowTotal)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </Card>
                      </>
                    )}
                  </div>
                )}

                {/* ── Por cajero ─────────────────────────────────────────── */}
                {view === 'cashier' && (
                  <div className="space-y-4">
                    {sortedCashiers.length === 0 ? (
                      <Text>{t.caja.empty}</Text>
                    ) : (
                      <>
                        {cashierBars.length > 0 && (
                          <Card>
                            <Title className="text-base">{t.caja.views.cashier}</Title>
                            <BarList
                              data={cashierBars}
                              valueFormatter={fmtMxn}
                              className="mt-3"
                            />
                          </Card>
                        )}
                        <Card className="p-0 overflow-hidden">
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableHeaderCell>{t.caja.cols.cashier}</TableHeaderCell>
                                  {tenderCodes.map((code) => (
                                    <TableHeaderCell key={code} className="text-right">
                                      {tenderNames.get(code) ?? code}
                                    </TableHeaderCell>
                                  ))}
                                  <TableHeaderCell className="text-right">{t.caja.cols.total}</TableHeaderCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sortedCashiers.map((c) => (
                                  <TableRow
                                    key={c.id}
                                    className="cursor-pointer hover:bg-tremor-background-muted dark:hover:bg-dark-tremor-background-muted"
                                    onClick={() =>
                                      setDrill({
                                        open: true,
                                        title: `${t.caja.drillTitle} — ${c.name}`,
                                        cashier: String(c.id),
                                      })
                                    }
                                  >
                                    <TableCell>{c.name}</TableCell>
                                    {tenderCodes.map((code) => (
                                      <TableCell key={code} className="text-right">
                                        {c.totals.has(code) ? fmtMxn(c.totals.get(code)!) : '—'}
                                      </TableCell>
                                    ))}
                                    <TableCell className="text-right font-medium">{fmtMxn(c.grand)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </Card>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Drill-down modal */}
            <CashTicketsModal
              open={drill.open}
              onClose={() => setDrill((d) => ({ ...d, open: false }))}
              from={from}
              to={to}
              sid={sid === null ? undefined : sid}
              date={drill.date}
              tender={drill.tender}
              cashier={drill.cashier}
              title={drill.title}
            />
          </>
        )}
      </main>
    </div>
  );
}
